import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchCollaboratorProfiles, usePendingApprovalsQuery } from '@/features/Vacations/hooks/data';
import { useManagerDayOffsQuery } from '@/features/DpRequests/hooks/data/useDpRequestQueries';
import type { DayOffRequest } from '../../../../../../shared/dpRequests';
import type { ApprovalItem, ApprovalKind } from '../approvals.types';
import { fromDayOff, fromVacation } from '../utils/approvalAdapters';

const PROFILES_STALE_TIME = 5 * 60 * 1000;

export function useApprovalItems(managerEmail?: string) {
    const vacations = usePendingApprovalsQuery(managerEmail);
    const dayOffs = useManagerDayOffsQuery(managerEmail);

    const dayOffRequests = useMemo(
        () => (dayOffs.data ?? []).filter((request): request is DayOffRequest => request.type === 'day_off'),
        [dayOffs.data]
    );

    const collaboratorIds = useMemo(
        () => Array.from(new Set(dayOffRequests.map((request) => request.collaboratorId))).sort(),
        [dayOffRequests]
    );

    const profiles = useQuery({
        queryKey: ['approval-day-off-profiles', collaboratorIds],
        queryFn: () => fetchCollaboratorProfiles(collaboratorIds).catch(() => new Map()),
        enabled: collaboratorIds.length > 0,
        staleTime: PROFILES_STALE_TIME,
    });

    const items = useMemo<ApprovalItem[]>(
        () => [
            ...vacations.approvals.map(fromVacation),
            ...dayOffRequests.map((request) => {
                const profile = profiles.data?.get(request.collaboratorId);
                return fromDayOff(request, {
                    role: profile?.positionName ?? null,
                    squad: profile?.departmentName ?? null,
                });
            }),
        ],
        [vacations.approvals, dayOffRequests, profiles.data]
    );

    const errors: Record<ApprovalKind, Error | null> = {
        vacation: (vacations.error as Error | null) ?? null,
        day_off: (dayOffs.error as Error | null) ?? null,
    };

    return {
        items,
        isLoading: vacations.isLoading || dayOffs.isLoading,
        errors,
    };
}
